import { ScheduledItem, ISODateString } from "@shared/types"
import {
  getOngoingOrUpcomingSchedule,
  sortSchedules,
  SCOPE_TYPES,
  CONTEXT_TYPES
} from "@shared/lib/schedule-utils"

import { endOf, isBeforeToday, isPast, startOf, toDate } from "@/lib/date-utils"
import i18n, { setLocale } from "@/store/i18n"
import * as localStorage from "@/lib/local-storage"

export const VIEW_TYPES = {
  WEEK: "week",
  MONTH: "month"
} as const

export const ANIMATION_DIRECTIONS = {
  FORWARD: "forward",
  BACKWARD: "backward"
} as const

export const FOR_YOU_FILTER = "_member"
export const TASKS_FILTER = "_tasks"

export const INDEXED_DATE_FORMAT = "YYYY-MM-DD"

export const formatDate = (date: string | Date): string =>
  toDate(date, INDEXED_DATE_FORMAT)

type PeriodUnit = typeof VIEW_TYPES[keyof typeof VIEW_TYPES]

// The strip is pinned to firstDay: 1, so the range must be Monday-start too.
const periodUnit = (unit: PeriodUnit) =>
  unit === VIEW_TYPES.WEEK ? "isoWeek" : unit

export const startOfPeriod = (
  unit: PeriodUnit,
  date: Date = new Date()
): Date => startOf(periodUnit(unit), date)

export const endOfPeriod = (unit: PeriodUnit, date: Date = new Date()): Date =>
  endOf(periodUnit(unit), date)

export const CONTEXTS = {
  GLOBAL: {
    id: "global",
    title: ""
  },
  SUBSCRIBER: {
    id: "subscriber",
    title: i18n.t("lib.scheduleUtil.forYou")
  }
} as const

export const ITEM_TYPES = {
  PAGE: "page",
  FORM: "form",
  TASK: "task"
  // TODO: Add other item types here
} as const

export const TASK_METRIC_TYPES = {
  NUMBER: "number",
  OPTIONS: "options",
  YES_NO: "yes_no"
} as const

export const getLastSchedule = (
  schedules: ScheduledItem[]
): ScheduledItem | undefined => {
  const sortedSchedules = sortSchedules(schedules)
  return sortedSchedules.length > 0
    ? sortedSchedules[sortedSchedules.length - 1]
    : undefined
}

// TODO: Spec
export const shownEndsAt = (
  scheduledItem: ScheduledItem
): ISODateString | string => {
  if (scheduledItem.is_multiday && scheduledItem.scope_type === "account_app") {
    // For scheduled items that are multiday and globally scheduled, we only want to show the item on the start day
    return scheduledItem.starts_on || scheduledItem.starts_at
  }

  return scheduledItem.ends_on || scheduledItem.ends_at
}

export const dayOfWeekInSchedule = async (
  language: string
): Promise<string[]> => {
  try {
    await setLocale(language)
  } catch {
    // Handle or log the error if needed, or leave a comment explaining why it's ignored.
  }

  return [
    String(i18n.t("lib.scheduleUtil.sunday")),
    String(i18n.t("lib.scheduleUtil.monday")),
    String(i18n.t("lib.scheduleUtil.tuesday")),
    String(i18n.t("lib.scheduleUtil.wednesday")),
    String(i18n.t("lib.scheduleUtil.thursday")),
    String(i18n.t("lib.scheduleUtil.friday")),
    String(i18n.t("lib.scheduleUtil.saturday"))
  ]
}

export const validateStart = (
  date: string | Date,
  isAllDay: boolean
): boolean => {
  return isAllDay ? isBeforeToday(date) : isPast(date)
}

export const setStorageCalendarView = (uuid: string, view: string) => {
  localStorage.set(localStorage.LS_KEYS.CALENDAR_VIEW + "-" + uuid, view)
}

export const getStorageCalendarView = (uuid: string): string => {
  return localStorage.get(localStorage.LS_KEYS.CALENDAR_VIEW + "-" + uuid)
}

export {
  sortSchedules,
  getOngoingOrUpcomingSchedule,
  SCOPE_TYPES,
  CONTEXT_TYPES
}
